import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import "./Auth.css";

export default function Signup() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    role: "parent",
    password: "",
    confirmPassword: "",
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => {
      const next = { ...previous, [name]: value };
      if (name === "role" && value !== "teacher") {
        setResumeFile(null);
      }
      return next;
    });
  };

  const handleResumeChange = (event) => {
    const file = event.target.files?.[0] || null;
    setResumeFile(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!String(form.phoneNumber || "").trim()) {
      setError("Phone number is required.");
      return;
    }

    if (form.password.length < 8) {
      setError(t("authPage.passwordLength"));
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError(t("authPage.passwordMatch"));
      return;
    }

    if (form.role === "teacher" && !resumeFile) {
      setError(t("authPage.resumeRequired"));
      return;
    }

    setLoading(true);

    try {
      const nextUser = await register({
        name: form.name,
        email: form.email,
        phoneNumber: form.phoneNumber,
        role: form.role,
        password: form.password,
        resume: resumeFile,
      });
      if (nextUser?.role === "teacher" && !nextUser.is_verified) {
        navigate("/pending-verification", { replace: true });
      } else {
        navigate("/");
      }
    } catch (err) {
      setError(err.message || t("authPage.signupError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="auth-shell">
      <div className="auth-card">
        <h1>{t("authPage.signupTitle")}</h1>
        <p>{t("authPage.signupLead")}</p>

        {error && <div className="auth-error">{error}</div>}

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="auth-field">
            <label htmlFor="name">{t("authPage.name")}</label>
            <input
              id="name"
              name="name"
              type="text"
              autoComplete="name"
              placeholder={t("authPage.namePlaceholder")}
              value={form.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="auth-field">
            <label htmlFor="role">{t("authPage.roleLabel")}</label>
            <select
              id="role"
              name="role"
              value={form.role}
              onChange={handleChange}
            >
              <option value="parent">{t("authPage.roleParent")}</option>
              <option value="teacher">{t("authPage.roleTeacher")}</option>
            </select>
          </div>

          {form.role === "teacher" && (
            <div className="auth-field">
              <label htmlFor="resume">{t("authPage.resumeLabel")}</label>
              <input
                id="resume"
                name="resume"
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleResumeChange}
                required
              />
            </div>
          )}

          <div className="auth-field">
            <label htmlFor="email">{t("authPage.email")}</label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder={t("authPage.emailPlaceholder")}
              value={form.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="auth-field">
            <label htmlFor="phoneNumber">{t("authPage.phoneLabel")}</label>
            <input
              id="phoneNumber"
              name="phoneNumber"
              type="tel"
              autoComplete="tel"
              inputMode="tel"
              placeholder={t("authPage.phonePlaceholder")}
              value={form.phoneNumber}
              onChange={handleChange}
              required
            />
          </div>

          <div className="auth-field">
            <label htmlFor="password">{t("authPage.password")}</label>
            <div className="password-wrap">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder={t("authPage.passwordPlaceholder")}
                value={form.password}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((current) => !current)}
                aria-label={
                  showPassword
                    ? t("authPage.hidePassword")
                    : t("authPage.showPassword")
                }
                title={
                  showPassword
                    ? t("authPage.hidePassword")
                    : t("authPage.showPassword")
                }
              >
                <i
                  className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>

          <div className="auth-field">
            <label htmlFor="confirmPassword">
              {t("authPage.confirmPassword")}
            </label>
            <div className="password-wrap">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder={t("authPage.confirmPasswordPlaceholder")}
                value={form.confirmPassword}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword((current) => !current)}
                aria-label={
                  showConfirmPassword
                    ? t("authPage.hidePassword")
                    : t("authPage.showPassword")
                }
                title={
                  showConfirmPassword
                    ? t("authPage.hidePassword")
                    : t("authPage.showPassword")
                }
              >
                <i
                  className={`fa-solid ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"}`}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>

          <div className="auth-actions">
            <button type="submit" className="btn primary" disabled={loading}>
              {loading ? t("authPage.signupLoading") : t("authPage.signupCta")}
            </button>
            <div className="auth-alt">
              {t("authPage.hasAccount")}{" "}
              <Link to="/login">{t("authPage.goLogin")}</Link>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}
